import React, { useCallback } from 'react';
import { ApolloError } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

import { MembershipVoucherFragment, Offer_Engagement_Type, Vouchers_Sort_By } from '@occc/graphql';
import { IPaginationProps } from '@occc-app/hooks/usePaginatedQuery';
import { routeCreator as awardCatalogRouteCreator } from '@occc-app/routes/AwardCatalog/utils';
import { programManagementGameRouteCreator as voucheredGameRouteCreator } from '@occc-app/routes/Game/utils';

import { Table, TableHeader, MakeTableFields, Row } from '@occc/ui-design-system';
import { useSortingParams } from '../Vouchers/hooks';

type VouchersTableProps = {
  vouchers?: MembershipVoucherFragment[];
  loading: boolean;
  error?: ApolloError;
  paginationProps: IPaginationProps;
  headers: TableHeader[];
  makeFields: MakeTableFields<{
    voucher: MembershipVoucherFragment;
  }>;
};

const VouchersTable: React.FC<VouchersTableProps> = (props) => {
  const { vouchers, loading, error, paginationProps, headers, makeFields } = props;
  const [sortingParams, setSortingParams] = useSortingParams();

  const navigate = useNavigate();
  const openOfferDetails = useCallback(
    (voucher: MembershipVoucherFragment) => {
      if (!voucher.offer) return;
      if (voucher.offer.engagementType === Offer_Engagement_Type.GameVoucher) {
        navigate(voucheredGameRouteCreator.details(voucher.offer.id));
        return;
      }
      navigate(awardCatalogRouteCreator.details(voucher.offer.id));
    },
    [navigate],
  );

  const rows: Row[] | null | undefined = vouchers?.map((voucher) => {
    return {
      key: voucher.id,
      fields: makeFields({ voucher }),
      onClick: () => openOfferDetails(voucher),
    };
  });

  return (
    <Table
      headers={headers}
      rows={rows}
      loading={loading}
      error={error}
      paginationProps={paginationProps}
      sortingProps={
        sortingParams
          ? {
              field: sortingParams.field,
              direction: sortingParams.direction,
              onClick: (field: Vouchers_Sort_By) => {
                if (sortingParams.field !== field) {
                  setSortingParams({ field, direction: 'desc' });
                  return;
                }
                setSortingParams({
                  field,
                  direction: sortingParams.direction === 'asc' ? 'desc' : 'asc',
                });
              },
            }
          : undefined
      }
    />
  );
};

export default VouchersTable;
